import type { ComponentPropsWithoutRef } from "react";
import styles from "./StatusDot.module.css";

/**
 * StatusDot — 8px indicator dot primitive.
 *
 * Matches the Figma StatusDot component (fileKey QkVMIm6TUdblppfqYlUtai,
 * node 1016:1492). A standalone colored circle used to indicate
 * connection / presence / sync status. Intended to sit alongside a
 * text label (e.g. in a HeaderBar next to a device name, or beside
 * a Notification's app name in the unread state) — it's NOT a Badge
 * (which has its own pill shape and lives as a separate primitive).
 *
 * Figma ships a single green variant (light: #10b981, dark: #34d399).
 * No online/offline/busy colors exist in the design file yet. If
 * design later adds more states, this primitive grows a `tone` prop
 * — for now we keep it single-shape, single-color and add tones on
 * demand. YAGNI wins.
 *
 * Accessibility:
 *   - Decorative by default — the dot alone isn't meaningful, it
 *     sits next to visible text that describes the state ("Connected",
 *     "Syncing", etc.). When used this way, omit `label` and the
 *     wrapper renders `aria-hidden="true"` so screen readers skip it
 *     and announce only the visible text.
 *   - When the dot is the ONLY indicator of state (compact UIs, no
 *     visible label), pass `label` — the wrapper renders
 *     `role="img"` + `aria-label` and assistive tech announces the
 *     current state. Matches Icon's label/decorative pattern exactly
 *     (Icon also uses `role="img"` for labeled static visuals, not
 *     `role="status"` which is an ARIA live region for dynamic
 *     announcements like toasts or Spinner).
 */

export type StatusDotProps = {
  /** Accessible label. When set, the dot announces as `role="img"`
   *  + `aria-label`. When omitted, the dot is decorative and carries
   *  `aria-hidden="true"`. */
  label?: string;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"span">,
  "className" | "children" | "aria-label" | "role" | "aria-hidden"
>;

export function StatusDot({ label, className, ...rest }: StatusDotProps) {
  const classes = [styles.statusDot, className].filter(Boolean).join(" ");
  const a11y = label ? { role: "img" as const, "aria-label": label } : { "aria-hidden": true };
  return <span className={classes} {...a11y} {...rest} />;
}
